import { Button, Icon, token } from "@may-i/mash-design-system";
import html2canvas from "html2canvas";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useGetAllBranches } from "~/_legacy/data-hooks/use-get-all-branches";
import { useGetAnalysisConfigsByBranch } from "~/_legacy/data-hooks/use-get-analysis-configs-by-branch";
import { useGetCameraMemosByBranch } from "~/_legacy/data-hooks/use-get-camera-memos-by-branch";
import { useGetCamerasByBranch } from "~/_legacy/data-hooks/use-get-cameras-by-branch";
import { useGetFloorPlanMemoDiagramsGroupedByFloorPlan } from "~/_legacy/data-hooks/use-get-floor-plan-memo-diagrams-grouped-by-floor-plan";
import { useGetFloorPlanMemoTextsGroupedByFloorPlan } from "~/_legacy/data-hooks/use-get-floor-plan-memo-texts-grouped-by-floor-plan";
import type { FloorPlanMemoDiagram, FloorPlanMemoText } from "~/_legacy/types/analysis";
import { useGetFloorPlansByBranch } from "~/_legacy/data-hooks/use-get-floor-plans-by-branch";
import { combineCamerasWithAnalysisConfigsAndCameraMemos } from "~/_legacy/utils/combine";
import { ActiveSelection, type TPointerEvent } from "fabric";
import { CameraObject } from "~/pages/camera-mapping/fabric-objects/CameraObject";
import {
  createFloorPlanMemoDiagramObject,
  createFloorPlanMemoTextObject,
  isFloorPlanMemoObject,
} from "~/pages/camera-mapping/fabric-objects/FloorPlanMemo";
import { MemoMarker } from "~/pages/camera-mapping/fabric-objects/MemoMarker";
import { useFloorPlanCanvas } from "~/pages/camera-mapping/useFloorPlanCanvas";
import { toProxiedImageUrl } from "~/shared/lib/image-proxy";
import { Kbd } from "~/shared/ui/Kbd";
import { AddAdjustGuideCameraFilter, type AddAdjustGuideFilter } from "./AddAdjustGuideCameraFilter";
import { AddAdjustGuideCameraMemoList } from "./AddAdjustGuideCameraMemoList";
import { AddAdjustGuideFloorPlanSelect } from "./AddAdjustGuideFloorPlanSelect";
import { SlotOverlay, type SlotPlacement } from "./SlotOverlay";
import { assignCamerasToSlots, computeSlotPoints, type SlotPoint } from "./slot-layout";

const DEFAULT_BACKGROUND_IMAGE_WIDTH = 1500;

function resizeCameraIconScale(bgScaledWidth: number, iconSize: number): number {
  return (iconSize * bgScaledWidth) / DEFAULT_BACKGROUND_IMAGE_WIDTH;
}

interface Props {
  onClose: () => void;
  branchUid: string;
  branchVersionId?: number;
}

export function AddAdjustGuideModal({ onClose, branchUid, branchVersionId }: Props) {
  const { t } = useTranslation("shared");

  const { data: branches = [] } = useGetAllBranches();
  const currentBranch = useMemo(
    () => branches.find((b) => b.uid === branchUid) ?? null,
    [branches, branchUid],
  );

  const { data: floorPlans = [], isLoading: isLoadingFloorPlans } = useGetFloorPlansByBranch({
    branchUid,
    branchVersionId,
  });
  const { data: cameras = [] } = useGetCamerasByBranch({ branchUid, branchVersionId });
  const { data: analysisConfigs = [] } = useGetAnalysisConfigsByBranch({ branchUid, branchVersionId });
  const { data: cameraMemos = [] } = useGetCameraMemosByBranch({ branchUid, branchVersionId });

  const localCamerasWithAnalysisConfig = useMemo(
    () => combineCamerasWithAnalysisConfigsAndCameraMemos(cameras, analysisConfigs, cameraMemos),
    [cameras, analysisConfigs, cameraMemos],
  );

  const [filter, setFilter] = useState<AddAdjustGuideFilter>({
    includesNotChanged: true,
    includesAdjusted: true,
    includesAdded: true,
  });
  const [selectedFloorPlanId, setSelectedFloorPlanId] = useState<number | null>(null);

  // 첫 도면 자동 선택
  useEffect(() => {
    if (selectedFloorPlanId == null && floorPlans.length > 0) {
      setSelectedFloorPlanId(floorPlans[0].id);
    }
  }, [floorPlans, selectedFloorPlanId]);

  // body 스크롤 잠금
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // 필터 변경 시 선택 초기화 — 필터 밖 카메라가 선택된 채 남는 문제 방지
  const filterRef = useRef(filter);
  useEffect(() => {
    if (filterRef.current !== filter) {
      filterRef.current = filter;
      setSelectedCameraIds([]);
    }
  }, [filter]);

  const filteredCamerasWithAnalysisConfig = useMemo(() => {
    return localCamerasWithAnalysisConfig.filter((c) => {
      const isAdjusted = !!c.adjustmentConfig?.position;
      const isAdded = !!c.additionConfig?.additionId;
      if (filter.includesNotChanged && !isAdded && !isAdjusted) return true;
      if (filter.includesAdjusted && isAdjusted) return true;
      if (filter.includesAdded && isAdded) return true;
      return false;
    });
  }, [localCamerasWithAnalysisConfig, filter]);

  const currentFloorPlan = useMemo(
    () => floorPlans.find((fp) => fp.id === selectedFloorPlanId) ?? null,
    [floorPlans, selectedFloorPlanId],
  );

  const floorPlanIdsForMemo = useMemo(
    () => floorPlans.map((fp) => fp.id),
    [floorPlans],
  );
  const { data: floorPlanMemoDiagramsByFp = {} } =
    useGetFloorPlanMemoDiagramsGroupedByFloorPlan({
      branchUid,
      branchVersionId,
      floorPlanIds: floorPlanIdsForMemo,
    });
  const { data: floorPlanMemoTextsByFp = {} } =
    useGetFloorPlanMemoTextsGroupedByFloorPlan({
      branchUid,
      branchVersionId,
      floorPlanIds: floorPlanIdsForMemo,
    });

  const camerasOnFloorPlan = useMemo(
    () => filteredCamerasWithAnalysisConfig.filter((c) => c.floorPlanId === selectedFloorPlanId),
    [filteredCamerasWithAnalysisConfig, selectedFloorPlanId],
  );

  // 캔버스
  const panningEnabledRef = useRef(true);
  const {
    fabricRef,
    canvasRef,
    containerRef,
    percentToPixel,
    getBackgroundScaledWidth,
    imageNaturalSize,
  } = useFloorPlanCanvas({
    imageUrl: toProxiedImageUrl(currentFloorPlan?.imageUrl),
    panningEnabledRef,
  });

  const outerRef = useRef<HTMLDivElement>(null);
  const [outerSize, setOuterSize] = useState({ w: 0, h: 0 });
  const [viewportRev, setViewportRev] = useState(0);
  const lastTransformRef = useRef<number[]>([1, 0, 0, 1, 0, 0]);

  // 외부 컨테이너 크기 추적 (슬롯 오버레이용)
  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      setOuterSize({ w: rect.width, h: rect.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // 뷰포트 변환 추적 (줌/팬 시 카메라 위치 재계산)
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const onAfterRender = () => {
      const vt = canvas.viewportTransform;
      const last = lastTransformRef.current;
      if (vt.some((v, i) => v !== last[i])) {
        lastTransformRef.current = [...vt];
        setViewportRev((r) => r + 1);
      }
    };
    canvas.on("after:render", onAfterRender);
    return () => {
      canvas.off("after:render", onAfterRender);
    };
  }, [fabricRef]);

  const [selectedCameraIds, setSelectedCameraIds] = useState<number[]>([]);
  const selectedCameraIdsRef = useRef<number[]>([]);
  selectedCameraIdsRef.current = selectedCameraIds;
  const isShiftPressedRef = useRef(false);
  const isProgrammaticSelectionRef = useRef(false);
  // 우리 click handler 가 React state 를 업데이트한 직후, fabric 이 후속 selection event 를 stale ActiveSelection 으로 발화해서 collectIds 가 잘못된 toggle 적용하는 것 방지
  const isClickInFlightRef = useRef(false);

  // Ctrl/Cmd: 카메라 다중 선택 모드 토글, Shift: 추가 선택용 플래그
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    // 카메라는 항상 marquee 후보(selectable=true)지만 클릭은 무시(evented=false).
    // canvas.selection은 Ctrl/Cmd 누른 동안만 true → 평소엔 pan, Ctrl 누르면 marquee.
    canvas.selection = false;

    const setSelectionMode = (enabled: boolean) => {
      panningEnabledRef.current = !enabled;
      canvas.selection = enabled;
      const active = canvas.getActiveObject();
      if (active) {
        active.evented = enabled;
      }
      canvas.requestRenderAll();
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Shift") isShiftPressedRef.current = true;
      if (e.key === "Control" || e.key === "Meta") setSelectionMode(true);
      if (e.key === "Escape") {
        setSelectedCameraIds([]);
        isProgrammaticSelectionRef.current = true;
        canvas.discardActiveObject();
        isProgrammaticSelectionRef.current = false;
        canvas.requestRenderAll();
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === "Shift") isShiftPressedRef.current = false;
      if (e.key === "Control" || e.key === "Meta") setSelectionMode(false);
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      setSelectionMode(false);
    };
  }, [fabricRef]);

  // 선택 변경 추적: Shift 누르면 기존 선택에 누적
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    const lockActive = () => {
      const active = canvas.getActiveObject();
      if (active && active instanceof ActiveSelection) {
        active.lockMovementX = true;
        active.lockMovementY = true;
        active.lockRotation = true;
        active.lockScalingX = true;
        active.lockScalingY = true;
        active.hasControls = false;
      }
    };

    const collectIds = () => {
      if (isProgrammaticSelectionRef.current) return;
      if (isClickInFlightRef.current) return; // click handler 직후 stale fabric event 무시
      lockActive();
      const newIds = canvas
        .getActiveObjects()
        .filter((obj): obj is CameraObject => obj instanceof CameraObject)
        .map((obj) => obj.cameraId);
      // Shift 누른 채로 marquee: 드래그 범위 카메라들을 기존 선택에 toggle (이미 있으면 제거, 없으면 추가)
      // Shift 없이 marquee: replace
      if (isShiftPressedRef.current) {
        const newIdSet = new Set(newIds);
        const prev = selectedCameraIdsRef.current;
        // 기존에 있던 것 중 newIds 에 포함된 것은 제거, 안 포함된 것은 유지
        const kept = prev.filter((x) => !newIdSet.has(x));
        // newIds 중 기존에 없던 것 추가
        const added = newIds.filter((x) => !prev.includes(x));
        setSelectedCameraIds([...kept, ...added]);
      } else {
        setSelectedCameraIds(newIds);
      }
    };
    const onCleared = () => {
      // 자동 해제 안 함
    };

    canvas.on("selection:created", collectIds);
    canvas.on("selection:updated", collectIds);
    canvas.on("selection:cleared", onCleared);
    return () => {
      canvas.off("selection:created", collectIds);
      canvas.off("selection:updated", collectIds);
      canvas.off("selection:cleared", onCleared);
    };
  }, [fabricRef]);

  // 카메라 렌더링 (조정 카메라는 조정 후 위치 + 원본 위치를 흐리게 함께 표시)
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !imageNaturalSize) return;

    const bgScaledWidth = getBackgroundScaledWidth();
    const iconScale = resizeCameraIconScale(bgScaledWidth, currentFloorPlan?.iconSize ?? 60);

    // 기존 카메라 모두 제거 후 다시 추가 (조정 카메라의 sub camera까지 동기화 단순화)
    const existing = canvas.getObjects().filter((o): o is CameraObject => o instanceof CameraObject);
    existing.forEach((o) => canvas.remove(o));

    camerasOnFloorPlan.forEach((camera) => {
      if (!camera.position) return;
      // 카메라 매핑 페이지와 동일 기준 — adjustmentConfig.position 이 있으면 조정으로 간주
      // (서버가 adjustment_id 가 빈 문자열이어도 position 이 있으면 조정 데이터로 본다는 레거시 정책)
      const cameraType = camera.adjustmentConfig?.position
        ? "edit"
        : camera.additionConfig?.additionId
          ? "add"
          : "default";
      const displayNumber = camera.analysisNumber;

      if (cameraType === "edit" && camera.adjustmentConfig) {
        // 조정 후 카메라 (메인, 선택 가능)
        const adjusted = camera.adjustmentConfig.position;
        const adjustedPx = percentToPixel({ x: adjusted.x, y: adjusted.y });
        const main = new CameraObject({
          cameraId: camera.id,
          cameraType: "edit",
          displayNumber,
          cameraIdPrefix: t("addAdjustGuideModal.cameraIdPrefix.adjustment"),
          left: adjustedPx.x,
          top: adjustedPx.y,
          angle: adjusted.rotation,
          iconScale,
          isFocused: false,
        });
        main.hasControls = false;
        main.lockMovementX = true;
        main.lockMovementY = true;
        main.lockRotation = true;
        main.selectable = true;
        main.evented = false;
        canvas.add(main);

        // 조정 전 카메라 (서브, 흐리게, 선택 불가) — 카메라 매핑과 동일하게 넘버링 유지
        const origPx = percentToPixel({ x: camera.position.x, y: camera.position.y });
        const sub = new CameraObject({
          cameraId: camera.id,
          cameraType: "default",
          displayNumber,
          cameraIdPrefix: null,
          left: origPx.x,
          top: origPx.y,
          angle: camera.position.rotation,
          iconScale,
          isFocused: false,
        });
        sub.opacity = 0.5;
        sub.selectable = false;
        sub.evented = false;
        (sub as CameraObject & { isSubCamera: boolean }).isSubCamera = true;
        canvas.add(sub);
      } else {
        const px = percentToPixel({ x: camera.position.x, y: camera.position.y });
        const cameraIdPrefix = cameraType === "add"
          ? t("addAdjustGuideModal.cameraIdPrefix.addition")
          : null;
        const obj = new CameraObject({
          cameraId: camera.id,
          cameraType,
          displayNumber,
          cameraIdPrefix,
          left: px.x,
          top: px.y,
          angle: camera.position.rotation,
          iconScale,
          isFocused: false,
        });
        obj.hasControls = false;
        obj.lockMovementX = true;
        obj.lockMovementY = true;
        obj.lockRotation = true;
        obj.selectable = true;
        obj.evented = false;
        canvas.add(obj);
      }
    });

    canvas.requestRenderAll();
  }, [
    fabricRef,
    imageNaturalSize,
    camerasOnFloorPlan,
    currentFloorPlan?.iconSize,
    getBackgroundScaledWidth,
    percentToPixel,
    t,
  ]);

  // 도면 메모(다이어그램·텍스트) 렌더링 — read-only
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !imageNaturalSize) return;

    const existing = canvas.getObjects().filter(isFloorPlanMemoObject);
    existing.forEach((obj) => canvas.remove(obj as never));

    if (selectedFloorPlanId == null) {
      canvas.requestRenderAll();
      return;
    }

    const ctx = { percentToPixel };
    const diagrams: FloorPlanMemoDiagram[] = floorPlanMemoDiagramsByFp[selectedFloorPlanId] ?? [];
    const texts: FloorPlanMemoText[] = floorPlanMemoTextsByFp[selectedFloorPlanId] ?? [];
    diagrams.forEach((memo) => canvas.add(createFloorPlanMemoDiagramObject(memo, ctx)));
    texts.forEach((memo) => canvas.add(createFloorPlanMemoTextObject(memo, ctx)));

    canvas.getObjects().forEach((obj) => {
      if (isFloorPlanMemoObject(obj)) canvas.sendObjectToBack(obj);
    });
    const bg = canvas.getObjects().find((o) => (o as { __isFloorPlanBg?: boolean }).__isFloorPlanBg);
    if (bg) canvas.sendObjectToBack(bg);

    canvas.requestRenderAll();
  }, [
    imageNaturalSize,
    selectedFloorPlanId,
    floorPlanMemoDiagramsByFp,
    floorPlanMemoTextsByFp,
    fabricRef,
    percentToPixel,
  ]);

  // 선택된 카메라 isFocused + fabric active selection 동기화
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    const allCameras = canvas
      .getObjects()
      .filter((o): o is CameraObject => o instanceof CameraObject)
      .filter((o) => !(o as CameraObject & { isSubCamera?: boolean }).isSubCamera);

    allCameras.forEach((obj) => {
      const next = selectedCameraIds.includes(obj.cameraId);
      if (obj.isFocused !== next) {
        obj.updateAppearance({ isFocused: next });
      }
    });

    const targets = allCameras.filter((o) => selectedCameraIds.includes(o.cameraId));
    const currentActive = canvas.getActiveObjects()
      .filter((o): o is CameraObject => o instanceof CameraObject)
      .map((o) => o.cameraId)
      .sort()
      .join(",");
    const desired = [...selectedCameraIds].sort().join(",");

    if (currentActive !== desired) {
      isProgrammaticSelectionRef.current = true;
      canvas.discardActiveObject();
      if (targets.length === 1) {
        const t = targets[0];
        t.evented = canvas.selection;
        canvas.setActiveObject(t);
      } else if (targets.length > 1) {
        const sel = new ActiveSelection(targets, { canvas });
        sel.lockMovementX = true;
        sel.lockMovementY = true;
        sel.lockRotation = true;
        sel.lockScalingX = true;
        sel.lockScalingY = true;
        sel.hasControls = false;
        sel.evented = canvas.selection;
        canvas.setActiveObject(sel);
      }
      isProgrammaticSelectionRef.current = false;
    }

    canvas.requestRenderAll();
  }, [fabricRef, selectedCameraIds]);

  // 메모 마커 렌더링
  // 캔버스의 메모 마커는 더 이상 표시하지 않음 — 우측 목록으로 정보 일원화
  // (선택/인터랙션 불가 상태로 노이즈만 됨)
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const existing = canvas.getObjects().filter((o): o is MemoMarker => o instanceof MemoMarker);
    if (existing.length === 0) return;
    existing.forEach((o) => canvas.remove(o));
    canvas.requestRenderAll();
  }, [fabricRef]);

  // 선택 시점에 결정된 슬롯 배정 (선택 변경 시에만 갱신)
  const [slotAssignment, setSlotAssignment] = useState<{
    cameraId: number;
    slot: SlotPoint;
  }[]>([]);

  // 카메라 viewport 좌표 계산 헬퍼
  const getCameraViewport = useCallback(
    (cameraId: number): { x: number; y: number } | null => {
      const canvas = fabricRef.current;
      if (!canvas) return null;
      const camera = camerasOnFloorPlan.find((c) => c.id === cameraId);
      if (!camera || !camera.position) return null;
      const pos = camera.adjustmentConfig?.position ?? camera.position;
      const world = percentToPixel({ x: pos.x, y: pos.y });
      const vt = canvas.viewportTransform;
      return {
        x: world.x * vt[0] + world.y * vt[2] + vt[4],
        y: world.x * vt[1] + world.y * vt[3] + vt[5],
      };
    },
    [camerasOnFloorPlan, percentToPixel, fabricRef],
  );

  // Ctrl/Cmd + 단일 클릭으로 카메라 토글 선택/해제
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    let downPoint: { x: number; y: number } | null = null;

    const onMouseDown = (opt: { e: MouseEvent | TouchEvent }) => {
      const e = opt.e as MouseEvent;
      downPoint = { x: e.clientX, y: e.clientY };
    };

    const onMouseUp = (opt: { e: MouseEvent | TouchEvent; target?: unknown }) => {
      const e = opt.e as MouseEvent;
      if (!(e.metaKey || e.ctrlKey)) return;
      if (!downPoint) return;
      const moved = Math.abs(e.clientX - downPoint.x) + Math.abs(e.clientY - downPoint.y);
      downPoint = null;
      if (moved > 5) return; // 드래그면 marquee 로 처리

      // fabric v7: findTarget 시그니처 변경 + selectable=true 미설정 객체는 hit-test 못함.
      // 직접 containsPoint 로 검사 (회전/스케일 고려). sub camera 는 제외 + 위에 있는 객체 우선.
      const cams = canvas
        .getObjects()
        .filter((o): o is CameraObject => o instanceof CameraObject)
        .filter((o) => !(o as CameraObject & { isSubCamera?: boolean }).isSubCamera);
      const pointer = canvas.getScenePoint(e as unknown as TPointerEvent);
      const hit = [...cams].reverse().find((c) => c.containsPoint(pointer));

      if (!hit) return;
      const id = hit.cameraId;
      const isShift = e.shiftKey;
      // click 처리 중 fabric 의 stale selection event 가 collectIds 호출하는 것 방지
      isClickInFlightRef.current = true;
      setSelectedCameraIds((prev) => {
        if (isShift) {
          // Shift+Cmd/Ctrl+Click: toggle (이미 있으면 제거, 없으면 추가)
          return prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
        }
        // Cmd/Ctrl+Click only: replace — 그 카메라만 선택
        return [id];
      });
      // 다음 frame 후 lock 해제 (fabric event flush + React sync useEffect 모두 끝난 시점)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          isClickInFlightRef.current = false;
        });
      });
    };

    canvas.on("mouse:down", onMouseDown);
    canvas.on("mouse:up", onMouseUp);
    return () => {
      canvas.off("mouse:down", onMouseDown);
      canvas.off("mouse:up", onMouseUp);
    };
  }, [fabricRef]);

  // 선택 변경 시 슬롯 배정 1회 결정 — 줌/팬에 영향 안 받음
  useEffect(() => {
    if (selectedCameraIds.length === 0 || outerSize.w === 0 || outerSize.h === 0) {
      setSlotAssignment([]);
      return;
    }
    const cameraVps = selectedCameraIds
      .map((id) => {
        const vp = getCameraViewport(id);
        return vp ? { id, vp } : null;
      })
      .filter((v): v is { id: number; vp: { x: number; y: number } } => v !== null);

    const slots = computeSlotPoints(outerSize.w, outerSize.h);
    if (slots.length === 0) {
      setSlotAssignment([]);
      return;
    }
    const assigned = assignCamerasToSlots(
      cameraVps.map((c) => c.vp),
      slots,
    );
    setSlotAssignment(
      assigned.map((slotIdx, camIdx) => ({
        cameraId: cameraVps[camIdx].id,
        slot: slots[slotIdx],
      })),
    );
    // 선택 + 컨테이너 크기 바뀔 때만 재배정. viewport 변화는 무시.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCameraIds, outerSize.w, outerSize.h]);

  // 매 viewport 변화마다 카메라 좌표만 재계산해서 placements 합성
  const slotPlacements: SlotPlacement[] = useMemo(() => {
    return slotAssignment
      .map(({ cameraId, slot }) => {
        const camera = camerasOnFloorPlan.find((c) => c.id === cameraId);
        const cameraViewport = getCameraViewport(cameraId);
        if (!camera || !cameraViewport) return null;
        return {
          camera,
          cameraViewport,
          slotViewport: slot.point,
          slotConnectingViewport: slot.connectingPoint,
        };
      })
      .filter((p): p is SlotPlacement => p !== null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotAssignment, camerasOnFloorPlan, getCameraViewport, viewportRev]);

  // 다운로드용 ref
  const downloadTargetRef = useRef<HTMLDivElement>(null);

  const handleDownload = async () => {
    const target = downloadTargetRef.current;
    if (!target) return;

    // CORS 안전하게 src를 data URL로 변환:
    // <img crossOrigin="anonymous">로 로드 → canvas drawImage → toDataURL.
    // fetch보다 관대하고, S3가 CORS 헤더만 보내주면 항상 작동.
    const loadAsDataUrl = (src: string): Promise<string | null> =>
      new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          try {
            const c = document.createElement("canvas");
            c.width = img.naturalWidth || img.width;
            c.height = img.naturalHeight || img.height;
            const ctx = c.getContext("2d");
            if (!ctx) return resolve(null);
            ctx.drawImage(img, 0, 0);
            resolve(c.toDataURL("image/png"));
          } catch {
            resolve(null);
          }
        };
        img.onerror = () => resolve(null);
        img.src = src;
      });

    // 1) 모든 <img>를 data URL로
    const imgs = target.querySelectorAll<HTMLImageElement>("img");
    const imgReplacements = new Map<string, string>();
    await Promise.all(
      Array.from(imgs).map(async (img) => {
        const src = img.src;
        if (!src || src.startsWith("data:")) return;
        const dataUrl = await loadAsDataUrl(src);
        if (dataUrl) imgReplacements.set(src, dataUrl);
      }),
    );

    // 2) fabric <canvas>(도면)는 toDataURL로 직접 스냅샷
    const canvasEls = target.querySelectorAll<HTMLCanvasElement>("canvas");
    const canvasSnapshots = new Map<HTMLCanvasElement, string>();
    canvasEls.forEach((c) => {
      try {
        canvasSnapshots.set(c, c.toDataURL("image/png"));
      } catch {
        // tainted — 해당 영역만 빈 스냅샷
      }
    });

    const result = await html2canvas(target, {
      useCORS: true,
      onclone: (doc) => {
        doc.querySelectorAll<HTMLImageElement>("img").forEach((cloned) => {
          const replacement = imgReplacements.get(cloned.src);
          if (replacement) cloned.src = replacement;
        });
        const clonedCanvases = doc.querySelectorAll<HTMLCanvasElement>("canvas");
        const originals = Array.from(canvasEls);
        clonedCanvases.forEach((clonedCanvas, idx) => {
          const original = originals[idx];
          const snapshot = original ? canvasSnapshots.get(original) : undefined;
          if (!snapshot) return;
          const replacement = doc.createElement("img");
          replacement.src = snapshot;
          replacement.style.cssText = clonedCanvas.style.cssText;
          replacement.width = clonedCanvas.width;
          replacement.height = clonedCanvas.height;
          clonedCanvas.parentNode?.replaceChild(replacement, clonedCanvas);
        });
      },
    });

    const image = result.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = image;
    link.download = t("addAdjustGuideModal.downloadFileName", {
      clientName: currentBranch?.client.name,
      branchName: currentBranch?.name,
    });
    link.click();
  };

  return (
    <ModalRoot>
      <Backdrop onClick={onClose} />
      <ModalContentWrapper>
        <Container>
          <Header>
            <HeaderTitle>{t("addAdjustGuideModal.title.textContent")}</HeaderTitle>
            <Buttons>
              <Controls>
                <AddAdjustGuideFloorPlanSelect
                  floorPlans={floorPlans}
                  selectedFloorPlanId={selectedFloorPlanId}
                  onChange={(id) => setSelectedFloorPlanId(id)}
                  loading={isLoadingFloorPlans}
                />
                <ButtonGroup>
                  <AddAdjustGuideCameraFilter filter={filter} onApply={setFilter} />
                  <DownloadButton variant="text" color="primary" size="small" startIcon="ic_download" onClick={handleDownload}>
                    {t("addAdjustGuideModal.downloadButton.textContent")}
                  </DownloadButton>
                </ButtonGroup>
              </Controls>
              <CloseButton onClick={onClose}>
                <Icon icon="ic_close" width={16} />
              </CloseButton>
            </Buttons>
          </Header>
          <Content ref={downloadTargetRef}>
            <Left>
              <BranchMetadata>
                {currentBranch?.client.name} {currentBranch?.name}
              </BranchMetadata>
              <CanvasWrapper ref={outerRef}>
                <CanvasInner ref={containerRef}>
                  <canvas ref={canvasRef} />
                </CanvasInner>
                <SlotOverlay
                  width={outerSize.w}
                  height={outerSize.h}
                  placements={slotPlacements}
                />
              </CanvasWrapper>
            </Left>
            <Right>
              <AddAdjustGuideCameraMemoList
                cameras={filteredCamerasWithAnalysisConfig}
                selectedFloorPlanId={selectedFloorPlanId}
              />
            </Right>
          </Content>
          <Footer>
            <FooterItem>
              <Kbd>Ctrl/Cmd</Kbd>
              <FooterDesc>{t("addAdjustGuideModal.shortcut.selectDesc")}</FooterDesc>
            </FooterItem>
            <FooterItem>
              <Kbd>Ctrl/Cmd</Kbd>
              <FooterPlus>+</FooterPlus>
              <Kbd>Shift</Kbd>
              <FooterDesc>{t("addAdjustGuideModal.shortcut.addDesc")}</FooterDesc>
            </FooterItem>
            <FooterItem>
              <Kbd>Esc</Kbd>
              <FooterDesc>{t("addAdjustGuideModal.shortcut.clearDesc")}</FooterDesc>
            </FooterItem>
          </Footer>
        </Container>
      </ModalContentWrapper>
    </ModalRoot>
  );
}

const ModalRoot = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
`;

const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
`;

const ModalContentWrapper = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: calc(100vw - 16px);
  height: calc(100vh - 16px);
  background-color: ${token("colors.gray.white")};
  padding: 24px;
  border-radius: 8px;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.56;
  color: ${token("colors.text.primary")};
`;

const Buttons = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DownloadButton = styled(Button)`
  svg {
    width: 14px;
    height: 14px;
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: ${token("colors.text.primary")};
  border-radius: 50%;
  flex-shrink: 0;

  &:hover {
    background: ${token("colors.action.hover")};
  }
`;

const Content = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 1fr 280px;
  gap: 8px;
  padding: 16px 12px;
  min-height: 0;
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
`;

const BranchMetadata = styled.div`
  align-self: center;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  color: ${token("colors.text.secondary")};
`;

const CanvasWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  flex: 1;
  min-height: 0;
`;

const CanvasInner = styled.div`
  position: absolute;
  inset: 0;
`;

const Right = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 20px;
`;

const FooterItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const FooterPlus = styled.span`
  font-size: 11px;
  color: #8f95b2;
`;

const FooterDesc = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 13px;
  line-height: 1.5;
  color: ${token("colors.text.secondary")};
`;
